import { useEffect, useRef, useCallback } from 'react';
import { analytics } from '../services/analytics';
import { extractImageUuid, findAttachmentByUrl } from '../utils/imageUtils';
import type { Attachment } from './useHomeAttachments';

interface UseImageEngagementTrackingOptions {
  userUuid?: string;
  homeId?: string;
  currentImageUrl?: string;
  attachments?: Attachment[];
  isVisible?: boolean; // Whether the image viewer is currently visible
}

/**
 * Hook to track image engagement time similar to Android implementation.
 * Automatically logs engagement time when:
 * - Image changes (dismissal_reason: "next_image")
 * - Component unmounts (dismissal_reason: "closed_carousel") 
 * - Page unloads (dismissal_reason: "page_unload")
 * - Tab becomes hidden (dismissal_reason: "tab_change")
 */
export function useImageEngagementTracking({
  userUuid,
  homeId,
  currentImageUrl,
  attachments = [],
  isVisible = true
}: UseImageEngagementTrackingOptions) {
  const currentImageStartTime = useRef<number>(Date.now());
  const currentImageRef = useRef<string | undefined>(currentImageUrl);
  const isVisibleRef = useRef(isVisible);
  const userUuidRef = useRef(userUuid);
  const homeIdRef = useRef(homeId);
  const attachmentsRef = useRef(attachments);

  // Update refs when values change
  userUuidRef.current = userUuid;
  homeIdRef.current = homeId;
  attachmentsRef.current = attachments;

  // Function to log engagement time for current image
  const logCurrentImageEngagement = useCallback((dismissalReason: string) => {
    if (!userUuidRef.current || !currentImageRef.current) return;
    
    const currentAttachment = findAttachmentByUrl(attachmentsRef.current, currentImageRef.current);
    if (currentAttachment) {
      const engagementTime = Math.max(0, Date.now() - currentImageStartTime.current);
      const cfUuid = extractImageUuid(currentAttachment.image_url || '');
      
      // Only log if we have meaningful engagement time (at least 50ms to avoid timing issues)
      if (engagementTime >= 50) {
        analytics.logImageEngagementTime(
          userUuidRef.current,
          homeIdRef.current || null,
          currentAttachment.key,
          cfUuid,
          engagementTime,
          dismissalReason
        );
        
        if (process.env.NODE_ENV === 'development') {
          console.log(
            `📊 Image engagement: ${engagementTime}ms for image ${currentAttachment.key} (cf_uuid: ${cfUuid}), reason: ${dismissalReason}`
          );
        }
      } else if (process.env.NODE_ENV === 'development') {
        console.log(
          `⚠️ Skipped logging very short engagement: ${engagementTime}ms for reason: ${dismissalReason}`
        );
      }
    }
  }, []); // No dependencies since we use refs

  // Track image changes
  useEffect(() => {
    // If image changed, log engagement for previous image
    if (currentImageRef.current && currentImageRef.current !== currentImageUrl && isVisibleRef.current) {
      logCurrentImageEngagement('next_image');
    }
    
    // Update refs and reset timer
    currentImageRef.current = currentImageUrl;
    currentImageStartTime.current = Date.now();
  }, [currentImageUrl, logCurrentImageEngagement]);

  // Track visibility changes (tab switching, etc.)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isVisibleRef.current) {
        // Tab is becoming hidden
        logCurrentImageEngagement('tab_change');
        // Reset timer for when tab comes back
        currentImageStartTime.current = Date.now();
      } else if (!document.hidden && isVisibleRef.current) {
        // Tab is becoming visible again, reset timer
        currentImageStartTime.current = Date.now();
      }
    };

    const handleBeforeUnload = () => {
      if (isVisibleRef.current) {
        logCurrentImageEngagement('page_unload');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [logCurrentImageEngagement]);

  // Track component visibility changes
  useEffect(() => {
    if (!isVisible && isVisibleRef.current) {
      // Component is becoming invisible
      logCurrentImageEngagement('closed_carousel');
    } else if (isVisible && !isVisibleRef.current) {
      // Component is becoming visible, reset timer
      currentImageStartTime.current = Date.now();
    }
    
    isVisibleRef.current = isVisible;
  }, [isVisible, logCurrentImageEngagement]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isVisibleRef.current && userUuidRef.current && currentImageRef.current) {
        // Use the same logic as logCurrentImageEngagement but inline to avoid stale closure
        const currentAttachment = findAttachmentByUrl(attachmentsRef.current, currentImageRef.current);
        if (currentAttachment) {
          const engagementTime = Math.max(0, Date.now() - currentImageStartTime.current);
          const cfUuid = extractImageUuid(currentAttachment.image_url || '');
          
          if (engagementTime >= 50) {
            analytics.logImageEngagementTime(
              userUuidRef.current,
              homeIdRef.current || null,
              currentAttachment.key,
              cfUuid,
              engagementTime,
              'closed_carousel'
            );
            
            if (process.env.NODE_ENV === 'development') {
              console.log(
                `📊 Image engagement (cleanup): ${engagementTime}ms for image ${currentAttachment.key} (cf_uuid: ${cfUuid}), reason: closed_carousel`
              );
            }
          } else if (process.env.NODE_ENV === 'development') {
            console.log(
              `⚠️ Skipped logging very short engagement (cleanup): ${engagementTime}ms for reason: closed_carousel`
            );
          }
        }
      }
    };
  }, []); // No dependencies needed since we use refs

  // Return function to manually log engagement (for custom dismissal reasons)
  return {
    logEngagement: logCurrentImageEngagement
  };
}